/**
 * Générateur — Utiliser les nombres premiers pour reconnaître et produire des
 * fractions égales.
 * Attendu ID : 155 | Niveau : 4ème | Domaine : Divisibilité (desc 10, O20 volet 1)
 *
 * Source doc prof : Ex.1-6. Plan de cartes validé 2026-07-14, resserré le
 * 2026-07-28, tel qu'il est depuis la revue du 2026-09-23 :
 *
 *   Carte « egales » — Laquelle de ces fractions est égale à … ? (qcm_unique)
 *     La fraction de l'énoncé et la bonne réponse sont deux écritures de a/b
 *     par deux premiers différents de 7 à 31 ; trois distracteurs nommés
 *     (REGLES_DISTRACTEURS_QCM).
 *   Carte « completer » — Produire des fractions égales
 *     Saisie trous (texte_trou_saisie) : deux ou trois égalités en lignes
 *     KaTeX, chacune « produire » ou « simplifier » par un premier de 7 à 19,
 *     UN trou entier par ligne.
 *     Saisie (texte, 2026-09-24) : une fraction égale à trouver sans cible,
 *     avec des termes plus petits (voir la révision en fin d'en-tête).
 *
 * ⚠️ MIGRATION 2026-07-28 — les cartes « signe », « reecrire » et « sanscalcul »
 * sont parties vers gen_146 (« il effectue, avec des nombres décimaux relatifs,
 * des produits et des quotients »). Motif : elles mobilisaient la règle des
 * signes, pas les nombres premiers ; l'attendu 146 les couvre explicitement
 * (exemple de réussite éduscol : « il détermine le signe de
 * (−6,7)×7×(−1,24)×(−0,7) et de 11,4×(−3,5)/−(5,6×123) »). Ne pas les
 * réintroduire ici.
 *
 * Écritures fermées partout : chaque trou n'admet qu'un entier.
 *
 * Revue 2026-09-23 (vague 153-163), attendu de fin de 4e : « Il utilise les
 * nombres premiers inférieurs à 100 pour reconnaître et produire des fractions
 * égales. » Exemple de réussite : « Il reconnaît les fractions égales parmi les
 * suivantes sans utiliser de calculatrice : 14/49 ; 22/55 ; 34/85 ; 62/155 »
 * (les trois dernières valent 2/5 par 11, 17 et 31 ; 14/49 vaut 2/7). Cours du
 * prof (19Fractions1) : des fractions égales se reconnaissent en simplifiant par
 * la décomposition ; les produits en croix ne sont pas la méthode (ils
 * appartiennent au cours de proportionnalité).
 *   - Les deux cartes faisaient le geste de 5e (multiplier haut et bas par k de
 *     2 à 6) : le facteur premier commun était 2, 3 ou 5 dans 86,3 % des
 *     tirages de « egales » et 100 % de « completer », jamais de 13 à 31. Tout
 *     est recadré sur des facteurs premiers communs de 7 à 31.
 *   - « egales » : « Laquelle de ces fractions est égale à (a·p)/(b·p) ? »,
 *     a/b irréductible (a < b ≤ 9), p premier de 7 à 31 ; la clé (a·p2)/(b·p2)
 *     avec p2 ≠ p ; distracteurs deux_coefficients, meme_numerateur_simplifie
 *     (le 14/49 de l'exemple) et conversion_additive (DEPP 2de 2025 q.8,
 *     23,3 %) ; termes ≤ 200. Ajout du correcteur : les premiers des
 *     distracteurs (p3, p4) ne reprennent pas celui de l'énoncé, sinon le
 *     distracteur garde un terme de la fraction posée et s'élimine sans
 *     calcul. Mesures après : aucune des heuristiques testées (plus petits
 *     termes, dénominateur multiple de b, seul premier commun ≥ 7,
 *     numérateur le plus partagé) ne désigne la clé seule. Sortent : le signe
 *     (décidable sans calcul dans 24,8 % des tirages, et hors attendu), le
 *     choix mort « On ne peut pas les comparer », la parenthèse « (Pense aux
 *     produits en croix.) ».
 *   - « completer » : deux ou trois lignes KaTeX (fractions empilées,
 *     fracTrouTex), chacune « produire » (2/5 = ?/85, 85 = 5 × 17) ou
 *     « simplifier » (34/85 = ?/5), p premier de 7 à 19, termes ≤ 160, bases
 *     distinctes par construction (4,1 % de lignes en double avant) ; consigne
 *     sans parenthèse.
 *   - Rappel brouillon IMPÉRATIF sur les deux cartes (RAPPEL_BROUILLON) :
 *     trouver le premier commun demande des divisions.
 *   - Titre de l'en-tête corrigé : il annonçait la méthode des produits en
 *     croix, périmée pour cet attendu.
 *
 * Révision 2026-09-24 (idée du prof, recadrée avec lui) :
 *   - Carte « completer » renommée « Produire des fractions égales » (les mots
 *     de l'attendu), clés inchangées.
 *   - Nouveau format Saisie (completer_texte) : « Donne une fraction égale à
 *     154/231, avec un numérateur et un dénominateur plus petits. » L'élève
 *     trouve lui-même le premier commun, sans cible donnée. Facteurs communs
 *     toujours des premiers de 7 à 31 (un premier, ou deux parmi 7, 11, 13 un
 *     tirage sur trois) : ni 2, ni 3, ni 5, donc pas de réussite par le geste
 *     de 5e. Toutes les bonnes réponses admises ((k·a)/(k·b), k de 1 à m − 1,
 *     liste finie en comparaison d'expression) ; la fraction posée, une
 *     fraction égale plus grande ou une fraction fausse sont refusées. La
 *     ligne grise « Pour la barre de fraction, tape /. » disparaît à
 *     l'impression.
 */

import {
    randomChoice, randInt, shuffle, fracTex, fracTrouTex,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { pgcd } from './premiers';

const ATTENDU_ID = 155;

const L_EGALES = 'Reconnaître des fractions égales';
// « Produire » : les mots de l'attendu (renommée le 2026-09-24, ancienne
// « Compléter des fractions égales » ; clés de format inchangées).
const L_COMPLETER = 'Produire des fractions égales';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (décision 2026-09-23), IMPÉRATIF sur tous les formats. Forme
// maison (147, 149) ; juste avant la question ; aucune méthode soufflée.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

// Consigne de clavier de la saisie : ligne grise retirée à l'impression comme le
// rappel brouillon (sur papier, l'élève écrit une fraction). Même forme que
// « Pour multiplier, tape *. » (154).
const AIDE_BARRE = '<p class="italic text-slate-400">Pour la barre de fraction, tape /.</p>';

// Premiers communs « de la liste » (au-delà des tables) : 7 à 31 pour le QCM,
// 7 à 19 pour les égalités à compléter.
const PREMIERS_7_31 = [7, 11, 13, 17, 19, 23, 29, 31];
const PREMIERS_7_19 = [7, 11, 13, 17, 19];

// Bases a/b irréductibles, a < b ≤ 9 (27 bases).
const BASES = (() => {
    const out = [];
    for (let b = 2; b <= 9; b++) for (let a = 1; a < b; a++) if (pgcd(a, b) === 1) out.push({ a, b });
    return out;
})();

// Second chemin : égalité de deux fractions par les produits (a × d = b × c),
// jamais montrée à l'élève, seulement pour vérifier la construction.
const egales = ([n1, d1], [n2, d2]) => n1 * d2 === n2 * d1;

// ═════════ Carte « egales » — Laquelle est égale ? (QCM) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    egales_qcm_unique: {
        support: 'fraction de l\'énoncé (a·p)/(b·p), a/b irréductible avec a < b ≤ 9, p premier de 7 à 31 ; clé (a·p2)/(b·p2) avec p2 premier de 7 à 31 différent de p ; tous les termes ≤ 200',
        familles: {
            deux_coefficients: '(a·p2)/(b·p3), p3 premier différent de p2 et de p : même numérateur que la clé, mais haut et bas multipliés par deux nombres différents',
            meme_numerateur_simplifie: '(a·p4)/(c·p4), c ≠ b, p4 premier différent de p2 et de p : se simplifie par un premier comme les autres et garde le numérateur simplifié a, mais pas le dénominateur (le 14/49 de l\'exemple de réussite, qui vaut 2/7 et non 2/5)',
            conversion_additive: '(a·p + m)/(b·p + m) : le même nombre m ajouté en haut et en bas de la fraction de l\'énoncé (DEPP 2de 2025 q.8, 23,3 %) ; m est choisi pour que le numérateur soit celui de la clé quand p2 > p, sinon entre 2 et 30',
        },
        supprimees: {
            on_ne_peut_pas_comparer: 'choix jamais juste et sans erreur nommée derrière',
            signe: 'variante « signe opposé », décidable sans calcul et hors de l\'attendu',
        },
        garantie: 'quatre choix garantis : la clé vaut a/b ; deux_coefficients vaut (a/b)·(p2/p3) ≠ a/b, meme_numerateur_simplifie vaut a/c ≠ a/b, conversion_additive vaut a/b seulement si m = 0 ou a = b (exclus) ; les quatre écritures deux à deux distinctes et différentes de l\'énoncé sont revérifiées (re-tirage borné, repli sur l\'exemple de réussite : 22/55, clé 34/85, 34/155, 14/49, 62/95) ; égalités revérifiées par les produits, throw sinon',
    },
};

const TERME_MAX_QCM = 200;

const tirerEgales = () => {
    for (let essai = 0; essai < 60; essai++) {
        const { a, b } = randomChoice(BASES);
        const premiers = PREMIERS_7_31.filter((q) => b * q <= TERME_MAX_QCM);
        if (premiers.length < 3) continue;
        const p = randomChoice(premiers);
        const p2 = randomChoice(premiers.filter((q) => q !== p));
        // p3 et p4 ne reprennent pas p : un distracteur qui garde le numérateur
        // ou le dénominateur de l'énoncé s'éliminerait sans calcul.
        const p3 = randomChoice(premiers.filter((q) => q !== p2 && q !== p));
        const cs = [];
        for (let c = a + 1; c <= 9; c++) if (c !== b && pgcd(a, c) === 1) cs.push(c);
        if (!cs.length) continue;
        const c = randomChoice(cs);
        const p4s = PREMIERS_7_31.filter((q) => c * q <= TERME_MAX_QCM && q !== p2 && q !== p);
        if (!p4s.length) continue;
        const p4 = randomChoice(p4s);
        const m = p2 > p ? a * (p2 - p) : randInt(2, 30);
        const tirage = {
            enonce: [a * p, b * p],
            cle: [a * p2, b * p2],
            deux_coefficients: [a * p2, b * p3],
            meme_numerateur_simplifie: [a * p4, c * p4],
            conversion_additive: [a * p + m, b * p + m],
            params: { a, b, p, p2, p3, c, p4, m },
        };
        const ecrits = [tirage.enonce, tirage.cle, tirage.deux_coefficients, tirage.meme_numerateur_simplifie, tirage.conversion_additive]
            .map(([x, y]) => `${x}/${y}`);
        const termes = ecrits.flatMap((s) => s.split('/').map(Number));
        if (new Set(ecrits).size === 5 && Math.max(...termes) <= TERME_MAX_QCM) return tirage;
    }
    // Repli déterministe : l'exemple de réussite (22/55 = 34/85 = 2/5).
    return {
        enonce: [22, 55], cle: [34, 85], deux_coefficients: [34, 155],
        meme_numerateur_simplifie: [14, 49], conversion_additive: [62, 95],
        params: { a: 2, b: 5, p: 11, p2: 17, p3: 31, c: 7, p4: 7, m: 40, repli: true },
    };
};

const FAMILLES_EGALES = ['deux_coefficients', 'meme_numerateur_simplifie', 'conversion_additive'];

const genEgalesQcm = () => {
    const t = tirerEgales();
    if (!egales(t.enonce, t.cle) || FAMILLES_EGALES.some((f) => egales(t.enonce, t[f]))) {
        throw new Error('gen_155 egales : égalités incohérentes');
    }
    const affiche = ([x, y]) => fracTex(x, y);
    const payload = makeQcm([t.cle, ...FAMILLES_EGALES.map((f) => t[f])].map(affiche), affiche(t.cle),
        RAPPEL_BROUILLON
        + `<p><b>Laquelle de ces fractions est égale à ${affiche(t.enonce)} ?</b></p>`,
        opts(L_EGALES));
    payload._meta.params = { ...t.params, enonce: t.enonce.join('/'), cle: t.cle.join('/'), distracteurs: FAMILLES_EGALES };
    return payload;
};

// ═════════ Carte « completer » — Produire ou simplifier (trous) ═════════

const TERME_MAX_TROUS = 160;

// Une égalité : la base a/b et son écriture (a·p)/(b·p). « produire » : de la
// base vers la grande fraction ; « simplifier » : de la grande vers la base.
// Un seul trou, au numérateur ou au dénominateur du membre de droite.
const ligneCompleter = ({ a, b }) => {
    const p = randomChoice(PREMIERS_7_19.filter((q) => b * q <= TERME_MAX_TROUS));
    const sens = randomChoice(['produire', 'simplifier']);
    const trouEnHaut = randomChoice([true, false]);
    const [gauche, droite] = sens === 'produire' ? [[a, b], [a * p, b * p]] : [[a * p, b * p], [a, b]];
    const num = trouEnHaut ? `{{${droite[0]}}}` : String(droite[0]);
    const den = trouEnHaut ? String(droite[1]) : `{{${droite[1]}}}`;
    if (!egales(gauche, droite)) throw new Error('gen_155 completer : égalité fausse');
    return {
        ligne: `$\\dfrac{${gauche[0]}}{${gauche[1]}} = ${fracTrouTex(num, den)}$`,
        trace: `${gauche.join('/')} = ${droite.join('/')} (${sens}, ${p}, trou ${trouEnHaut ? 'haut' : 'bas'})`,
    };
};

const genCompleterTrous = () => {
    // Bases distinctes par construction (tirage sans remise dans les 27 bases).
    const bases = shuffle(BASES).slice(0, randInt(2, 3));
    const lignes = bases.map(ligneCompleter);
    const payload = makeTexteTrou(lignes.map((l) => l.ligne).join('\n'),
        RAPPEL_BROUILLON
        + '<p><b>Complète pour que les deux fractions de chaque ligne soient égales.</b></p>',
        { ...opts(L_COMPLETER), mode: 'saisie' });
    payload._meta.params = { egalites: lignes.map((l) => l.trace) };
    return payload;
};

// ═════════ Carte « completer » — Produire sans cible (saisie) ═════════
//
// « Donne une fraction égale à 154/231, avec un numérateur et un dénominateur
// plus petits. » (idée du prof, 2026-09-24, recadrée : sans « plus petits »,
// multiplier par 2 suffisait, le geste de 5e sorti de 155 le 23/09). La
// fraction posée est (a·m)/(b·m), a/b irréductible, m = un premier de 7 à 31
// ou le produit de deux premiers parmi 7, 11, 13 : ses facteurs communs sont
// TOUS des premiers de la liste, jamais 2, 3 ou 5. Les bonnes réponses sont
// exactement les (k·a)/(k·b) pour k de 1 à m − 1 (a/b irréductible) : en
// nombre FINI, donc la comparaison d'expression suffit (liste des écritures,
// aucun réglage nouveau). « 154/231 = 22/33 » est admis aussi.
const TERME_MAX_UN_PREMIER = 200;
const TERME_MAX_DEUX_PREMIERS = 300;
// Deux premiers parmi 7, 11, 13, un même premier pouvant revenir (7 × 7 :
// 98/147 se simplifie par 7, puis encore par 7). Sans la répétition, le régime
// ne comptait que 7 fractions, dont 5 de numérateur m (91/182 : on voit 182 =
// 2 × 91 sans chercher de premier) ; avec, 19 (mesure 2026-09-24).
const PAIRES_7_13 = [[7, 7], [7, 11], [7, 13], [11, 11], [11, 13], [13, 13]];
// Part des tirages à deux premiers communs (plusieurs chemins : par 7, par 11,
// ou par 77). Le professeur change ce nombre ici et nulle part ailleurs.
const PART_DEUX_PREMIERS = 1 / 3;

const tirerProduire = () => {
    if (Math.random() < PART_DEUX_PREMIERS) {
        const choix = [];
        PAIRES_7_13.forEach(([p, q]) => BASES.forEach(({ a, b }) => {
            if (b * p * q <= TERME_MAX_DEUX_PREMIERS) choix.push({ a, b, premiers: [p, q] });
        }));
        return randomChoice(choix);
    }
    const { a, b } = randomChoice(BASES.filter(({ b: den }) => den * 7 <= TERME_MAX_UN_PREMIER));
    const p = randomChoice(PREMIERS_7_31.filter((q) => b * q <= TERME_MAX_UN_PREMIER));
    return { a, b, premiers: [p] };
};

// Les plus petits facteurs premiers d'un entier (pour la vérification).
const facteursPremiers = (n) => {
    const out = [];
    let m = n;
    for (let d = 2; d * d <= m; d++) while (m % d === 0) { out.push(d); m /= d; }
    if (m > 1) out.push(m);
    return out;
};

const genProduireSaisie = () => {
    const { a, b, premiers } = tirerProduire();
    const m = premiers.reduce((x, y) => x * y, 1);
    const [N, D] = [a * m, b * m];
    const reponses = [];
    for (let k = 1; k < m; k++) reponses.push([k * a, k * b]);
    // Second chemin : chaque réponse est égale (produits), plus petite terme à
    // terme, différente de la fraction posée ; les facteurs communs de N et D
    // sont tous des premiers de 7 à 31.
    if (reponses.some(([x, y]) => !egales([x, y], [N, D]) || x >= N || y >= D)
        || facteursPremiers(pgcd(N, D)).some((f) => f < 7)) {
        throw new Error(`gen_155 produire : ${N}/${D} incohérent`);
    }
    const ecritures = reponses.flatMap(([x, y]) => [`${x}/${y}`, `${N}/${D}=${x}/${y}`]);
    const payload = makeSaisie(fracTex(a, b),
        RAPPEL_BROUILLON
        + `<p><b>Donne une fraction égale à ${fracTex(N, D)}, avec un numérateur et un dénominateur plus petits.</b></p>`
        + AIDE_BARRE,
        { ...opts(L_COMPLETER), strict: true, comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = { enonce: `${N}/${D}`, base: `${a}/${b}`, premiers, reponses: reponses.length };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    egales_qcm_unique: genEgalesQcm,
    completer_texte_trou_saisie: genCompleterTrous,
    completer_texte: genProduireSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous', texte: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'egales', label: L_EGALES, formatsList: buildFormats('egales') },
    { id: 'completer', label: L_COMPLETER, formatsList: buildFormats('completer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
